import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Selecciona...',
  className = '',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);

  // Cerrar al click fuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(prev => !prev)}
        className={`
          w-full flex items-center gap-3 px-4 py-2.5
          bg-brand-surface border border-brand-border rounded-xl
          text-sm text-brand-dark text-left overflow-hidden
          focus:outline-none focus:ring-2 focus:ring-brand-yellow
          transition-colors hover:border-brand-yellow/50
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        <span className={`flex-1 truncate ${selected ? 'text-brand-dark' : 'text-brand-text-soft'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          className={`w-4 h-4 text-brand-mid flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <polyline points="6 9 12 15 18 9" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown — min-width para que nunca sea más angosto que las opciones */}
      {open && (
        <div className="
          absolute z-50 left-0 min-w-full mt-1
          bg-brand-surface border border-brand-border rounded-xl
          shadow-xl overflow-hidden
          animate-in fade-in-0 zoom-in-95 duration-100
        "
          style={{ minWidth: 'max-content' }}
        >
          {options.map(option => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => { onChange(option.value); setOpen(false); }}
                className={`
                  w-full text-left px-4 py-2.5 text-sm transition-colors whitespace-nowrap
                  flex items-center justify-between gap-6
                  ${isSelected
                    ? 'bg-brand-yellow text-brand-dark font-semibold'
                    : 'text-brand-dark hover:bg-brand-light'
                  }
                `}
              >
                {option.label}
                {isSelected && (
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
